import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, Clock } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingNotice, setPendingNotice] = useState("");

  const isAdminHint = new URLSearchParams(loc.search).get("admin") === "1";
  const justRegistered = loc.state?.justRegistered;

  // If already signed in, jump straight to the right home
  useEffect(() => {
    if (user) {
      if (user.role === "admin") nav("/admin", { replace: true });
      else if (user.status === "pending_payment") nav("/payment", { replace: true });
      else if (!user.profile_complete) nav("/profile/setup", { replace: true });
      else nav("/dashboard", { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // If ?admin=1, prefill admin email
  useEffect(() => {
    if (isAdminHint && !email) setEmail("admin@trix27.com");
    // eslint-disable-next-line
  }, [isAdminHint]);

  useEffect(() => {
    if (justRegistered && loc.state?.email) setEmail(loc.state.email);
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setPendingNotice("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u.role === "admin") nav("/admin");
      else if (u.status === "pending_payment") nav("/payment");
      else if (!u.profile_complete) nav("/profile/setup");
      else nav("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Login failed";
      if (status === 403 && /activation/i.test(detail)) {
        setPendingNotice(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img alt="" src="https://images.pexels.com/photos/20898616/pexels-photo-20898616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1E5B]/85 via-[#0F1E5B]/40 to-transparent" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-end text-white">
          <div className="font-editorial text-5xl leading-tight">Welcome back to<br /><span className="italic text-[#BBF7D0]">your studio.</span></div>
          <div className="mt-4 text-white/80 max-w-md">Sign in to continue crafting itineraries and unlock your saved drafts.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-14 bg-[#FCFCFC]">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center mb-10">
            <img
              src="/brand/logo.png"
              alt="Traveloble"
              className="h-60 w-auto select-none"
              draggable="false"
            />
          </Link>
          <div className="overline mb-2">{isAdminHint ? "Admin login" : "Agent & Admin login"}</div>
          <h1 className="font-editorial text-4xl text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-2">New to Traveloble? <Link to="/register" className="text-[#16A34A] underline underline-offset-4">Create an account</Link></p>

          {justRegistered && (
            <div data-testid="just-registered-notice" className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <b>Account created.</b> Sign in below to pick a subscription plan and request activation. Our team will approve your account once payment is verified.
            </div>
          )}

          {pendingNotice && (
            <div data-testid="pending-activation-notice" className="mt-6 rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3 items-start">
              <Clock size={16} className="mt-0.5 shrink-0 text-amber-700" />
              <div>
                <div className="font-semibold text-amber-900">Awaiting admin approval</div>
                <div className="mt-1 leading-relaxed">{pendingNotice}</div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="w-full border border-border rounded px-3.5 py-2.5 text-sm" placeholder="you@agency.com" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="w-full border border-border rounded px-3.5 py-2.5 text-sm" placeholder="••••••••" />
              <div className="mt-1.5 text-right">
                <a href="/forgot-password" data-testid="login-forgot-password-link"
                   className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline">
                  Forgot password?
                </a>
              </div>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={busy} className="btn-bronze w-full">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
